import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search, ShieldAlert, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { z } from 'zod';

import { resultCount } from '@/lib/result-count';

import { MIN_SEARCH_TERM_LENGTH, useDebouncedCallback } from '@/lib/useDebouncedCallback';

import { DataTable, type Column, type SortState } from '@/components/data/DataTable';
import { PageHeader } from '@/components/layout/PageHeader';
import { RequireCapability } from '@/components/layout/RequireCapability';
import { EntityChip } from '@/components/primitives/EntityChip';
import { LiveDataIndicator } from '@/components/primitives/LiveDataIndicator';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { QUERY_KIND_LABELS, useUserList, type UserListRow } from '@/features/users/users';

/**
 * Filters live in the URL for the same reason they do on the leagues list: an
 * operator mid-incident pastes the view they are looking at into a Slack thread.
 */
const searchSchema = z.object({
  q: z.string().optional(),
  internal: z.enum(['exclude', 'include', 'only']).default('exclude'),
  removed: z.enum(['exclude', 'include', 'only']).default('exclude'),
  sort: z.string().default('lastActiveAt'),
  direction: z.enum(['asc', 'desc']).default('desc'),
  page: z.coerce.number().default(1),
});

export const Route = createFileRoute('/users/')({
  validateSearch: searchSchema,
  component: UsersScreen,
});

/** Relative age, coarse on purpose — exact timestamps are on the detail screen. */
function relativeAge(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${String(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)}h`;
  const days = Math.floor(hours / 24);
  if (days < 365) return `${String(days)}d`;
  return `${String(Math.floor(days / 365))}y`;
}

function UsersScreen() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  /**
   * A term below the API's minimum is refused there rather than scanned for, so the
   * request is not sent at all. Derived from the URL (the committed value), not the
   * text box, so it matches what would actually go over the wire.
   */
  const committedTooShort =
    (search.q?.trim().length ?? 0) > 0 &&
    (search.q?.trim().length ?? 0) < MIN_SEARCH_TERM_LENGTH;

  const { data, isPending, isFetching, isError, error, refetch, dataUpdatedAt } = useUserList(
    {
      q: search.q,
      internal: search.internal,
      removed: search.removed,
      sort: search.sort,
      direction: search.direction,
      page: search.page,
    },
    !committedTooShort,
  );

  /*
    The text box is local state; the URL is written on a debounce.

    This search is a collection scan on a ZazzBackend-owned collection — the same
    database the live consumer app reads from — so one request per keystroke is real
    load on production, not just a wasted round trip. The URL stays the source of
    truth so a pasted link and a reload are unchanged.
  */
  const [term, setTerm] = useState(search.q ?? '');
  // The last value this input itself produced. Comparing the URL against it is what
  // separates "the debounce just landed" from "the URL changed underneath us".
  const sentRef = useRef(search.q ?? '');

  // Adjust during render rather than in an effect: an effect would fire a second
  // render pass, and syncing state from props in one is the cascade React warns
  // about. The guard means this only runs on a genuine external change —
  // back/forward, a pasted link, or the Clear button — never on our own commit.
  const urlTerm = search.q ?? '';
  if (urlTerm !== sentRef.current) {
    sentRef.current = urlTerm;
    setTerm(urlTerm);
  }

  const commitTerm = useDebouncedCallback((value: string) => {
    setSearch({ q: value || undefined });
  }, 300);

  const termTooShort = term.trim().length > 0 && term.trim().length < MIN_SEARCH_TERM_LENGTH;

  function setSearch(next: Record<string, unknown>) {
    void navigate({
      search: (previous) => ({ ...previous, ...next, page: (next.page as number) ?? 1 }),
      replace: true,
    });
  }

  const columns: Column<UserListRow>[] = [
    {
      id: 'name',
      header: 'User',
      width: 'minmax(200px, 1.6fr)',
      mobile: 'primary',
      cell: (user) => (
        <div className="min-w-0">
          <EntityChip
            kind="user"
            id={user.id}
            name={user.name}
            secondary={user.username ? `@${user.username}` : undefined}
            imageUrl={user.avatarUrl ?? undefined}
            to={`/users/${user.id}`}
          />
          {(user.isInternal || user.isRemoved) && (
            <div className="mt-1 flex flex-wrap gap-1">
              {user.isRemoved && <FlagBadge label="Deactivated" tone="bad" />}
              {user.isInternal && <FlagBadge label="Internal" tone="info" />}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      width: 'minmax(0, 1.4fr)',
      mobile: 'secondary',
      cell: (user) => <Contact value={user.email} verified={user.emailVerified} />,
    },
    {
      id: 'phone',
      header: 'Phone',
      width: '160px',
      mobile: 'secondary',
      cell: (user) => <Contact value={user.phone} verified={user.phoneVerified} mono />,
    },
    {
      id: 'tokenBalance',
      header: 'Tokens',
      width: '92px',
      align: 'right',
      mobile: 'detail',
      cell: (user) => (
        <span className="mesh-mono text-tx2">
          {user.tokenBalance === null ? '—' : user.tokenBalance.toLocaleString()}
        </span>
      ),
    },
    {
      id: 'lastActiveAt',
      header: 'Last active',
      width: '110px',
      sortable: true,
      align: 'right',
      mobile: 'secondary',
      cell: (user) => (
        <span
          className="mesh-mono"
          style={{ color: user.lastActiveAt ? 'var(--tx2)' : 'var(--tx3)' }}
          title={user.lastActiveAt ?? 'never seen'}
        >
          {relativeAge(user.lastActiveAt)}
        </span>
      ),
    },
    {
      id: 'createdAt',
      header: 'Joined',
      width: '110px',
      sortable: true,
      align: 'right',
      mobile: 'detail',
      cell: (user) => (
        <span className="mesh-mono text-tx3">
          {user.createdAt ? user.createdAt.slice(0, 10) : '—'}
        </span>
      ),
    },
  ];

  const sort: SortState = { columnId: search.sort, direction: search.direction };
  const hasNonDefaultFilters = search.internal !== 'exclude' || search.removed !== 'exclude';

  return (
    <RequireCapability capability="users.view">
      <div className="flex flex-col gap-3.5">
        <PageHeader
          eyebrow="User lookup"
          title={
            data
              ? resultCount(data.total, data.hasMore, data.rows.length, 'user', 'users')
              : 'Users'
          }
          meta={
            data ? (
              <span>
                {data.rows.length} shown
                {data.queryKind && (
                  <span className="text-tx3">
                    {' · '}
                    {QUERY_KIND_LABELS[data.queryKind].toLowerCase()}
                  </span>
                )}
              </span>
            ) : undefined
          }
          actions={
            <LiveDataIndicator
              updatedAt={dataUpdatedAt || undefined}
              isFetching={isFetching}
              onRefresh={() => void refetch()}
            />
          }
        />

        <div className="border-bd bg-card flex flex-col gap-2.5 rounded-xl border p-3">
          <div className="flex items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <Search
                size={14}
                aria-hidden
                className="text-tx3 pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2"
              />
              <input
                type="search"
                value={term}
                onChange={(event) => {
                  setTerm(event.target.value);
                  sentRef.current = event.target.value;
                  commitTerm(event.target.value);
                }}
                placeholder="Username, email, phone, name or ObjectId…"
                aria-label="Search users"
                className="border-bd bg-bg-alt text-tx placeholder:text-tx3 focus-visible:border-bd-s h-11 w-full rounded-lg border pr-2.5 pl-8 text-[13px] outline-none"
              />
            </div>
            {hasNonDefaultFilters && (
              <button
                type="button"
                onClick={() => setSearch({ internal: 'exclude', removed: 'exclude' })}
                className="border-bd text-tx2 hover:bg-elev hover:text-tx flex h-11 flex-none items-center gap-1.5 rounded-lg border px-2.5 text-xs"
              >
                <X size={12} aria-hidden />
                Reset
              </button>
            )}
          </div>

          {/* The API refuses a term this short rather than scanning; say so before it does. */}
          {termTooShort && (
            <p className="text-tx3 text-[11.5px]">
              Keep typing — searches run from {MIN_SEARCH_TERM_LENGTH} characters.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <FilterSelect
              label="Internal"
              value={search.internal}
              onChange={(value) => setSearch({ internal: value })}
              options={[
                { value: 'exclude', label: 'Internal: hidden' },
                { value: 'include', label: 'Internal: shown' },
                { value: 'only', label: 'Internal: only' },
              ]}
            />
            <FilterSelect
              label="Deactivated"
              value={search.removed}
              onChange={(value) => setSearch({ removed: value })}
              options={[
                { value: 'exclude', label: 'Deactivated: hidden' },
                { value: 'include', label: 'Deactivated: shown' },
                { value: 'only', label: 'Deactivated: only' },
              ]}
            />
          </div>

          {/*
            Stated rather than left implicit: an operator searching for an account a
            user says is broken will otherwise read "no results" as "no such user",
            when the row is simply filtered out by a default they did not set.
          */}
          {!hasNonDefaultFilters && (
            <p className="text-tx3 text-[11.5px]">
              Internal and deactivated accounts are hidden by default.
            </p>
          )}
        </div>

        {committedTooShort ? null : isError ? (
          <ErrorState error={error} subject="users" onRetry={() => void refetch()} />
        ) : (
          <>
            <DataTable
              label="Users"
              columns={columns}
              rows={data?.rows ?? []}
              rowKey={(user) => user.id}
              isLoading={isPending}
              sort={sort}
              onSortChange={(next) =>
                setSearch({ sort: next.columnId, direction: next.direction })
              }
              emptyState={
                search.q ? (
                  <EmptyState
                    icon={ShieldAlert}
                    title="No users match"
                    description={
                      hasNonDefaultFilters
                        ? 'Nothing matched that search with the current filters.'
                        : 'Nothing matched. Internal and deactivated accounts are hidden — try showing them before concluding the account is gone.'
                    }
                  />
                ) : (
                  <EmptyState
                    icon={Search}
                    title="Search for a user"
                    description="Paste a username, email, phone number, name or ObjectId. Phone numbers match on the last 10 digits, so the country code is optional."
                  />
                )
              }
            />

            {data && data.total !== null && data.total > data.pageSize && (
              <Pagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                onPage={(page) => setSearch({ page })}
              />
            )}
          </>
        )}
      </div>
    </RequireCapability>
  );
}

function Contact({
  value,
  verified,
  mono = false,
}: {
  value: string | null;
  verified: boolean;
  mono?: boolean;
}) {
  if (!value) return <span className="text-tx3">—</span>;

  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className={mono ? 'mesh-mono text-tx2 truncate' : 'text-tx2 truncate'}>
        {value}
      </span>
      {/*
        Only the positive case is marked. An unverified address is the common case,
        not a fault, and badging it would make every second row look like a problem.
      */}
      {verified && (
        <span
          aria-label="verified"
          title="Verified"
          className="flex-none text-[10px]"
          style={{ color: 'var(--ok)' }}
        >
          ✓
        </span>
      )}
    </span>
  );
}

function FlagBadge({ label, tone }: { label: string; tone: 'bad' | 'info' }) {
  return (
    <span
      className="font-head inline-flex items-center rounded px-1.5 py-px text-[9.5px] font-semibold uppercase"
      style={{
        letterSpacing: '0.1em',
        background:
          tone === 'bad' ? 'rgba(var(--err-rgb), 0.14)' : 'rgba(var(--info-rgb), 0.14)',
        color: tone === 'bad' ? 'var(--err-soft)' : 'var(--info-soft)',
      }}
    >
      {label}
    </span>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
        className="border-bd bg-bg-alt text-tx2 h-9 rounded-lg border px-2 text-[12.5px] outline-none"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="mesh-mono text-tx3 text-[11.5px]">
        {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="border-bd bg-elev text-tx2 hover:text-tx min-h-11 rounded-lg border px-3 text-[13px] disabled:opacity-40"
        >
          Prev
        </button>
        <span className="mesh-mono text-tx3 text-[11.5px]">
          {page} / {lastPage}
        </span>
        <button
          type="button"
          disabled={page >= lastPage}
          onClick={() => onPage(page + 1)}
          className="border-bd bg-elev text-tx2 hover:text-tx min-h-11 rounded-lg border px-3 text-[13px] disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}
